import { aiFilterSearchRequest } from '../CustomDash/filters/ai-search/ai-search-api-request';
import { screenerAiSearchRequest } from '../CustomDash/filters/ai-search/screener-ai-search-api-request';
import type {
  GlobalAssistantResolvedRouteFilters,
  GlobalAssistantRouteFilterKind,
} from './global-ai-assistant-route-filter-kind';
import type { AssistantTaxonomy } from './use-global-ai-assistant-taxonomy';

/**
 * Eager route-filter resolution cache.
 *
 * Two tiers:
 * 1. In-memory promise cache — dedupes in-flight requests within a tab.
 * 2. localStorage cache — persists RESOLVED responses so repeat visits /
 *    clicks on hydrated-session chips don't re-hit the API.
 *
 * Cache key includes `filterKind` so Talent Discovery v2 and screener
 * responses never collide for the same prompt.
 *
 * Taxonomy is part of the key because the AI filter endpoint's response
 * depends on it: role/skill for TD, and the geography taxonomy for BOTH —
 * a metro-resolved artifact must never be served to an administrative user
 * (or vice versa) after a preference switch.
 *
 * Bump `CACHE_VERSION` when the stored envelope or semantics change.
 */

/**
 * Bump this when the cached shape could misapply:
 *
 * 1. Upstream response shape changes for TD or screener apply paths.
 * 2. The prompt / taxonomy contract to either endpoint changes materially.
 * 3. This module's own stored envelope evolves.
 */
const CACHE_VERSION = '2026-08-20-1';

const STORAGE_KEY = 'revelio.globalAiAssistant.routeFilterCache.v2';
const MAX_ENTRIES = 30;

type FilterPromise = Promise<GlobalAssistantResolvedRouteFilters>;

type StoredEntry = {
  key: string;
  resolved: GlobalAssistantResolvedRouteFilters;
  cacheVersion: string;
};

function buildKey(
  prompt: string,
  tax: AssistantTaxonomy,
  filterKind: GlobalAssistantRouteFilterKind,
  activitiesEnabled: boolean
): string {
  // `?? 1` — an absent geography taxonomy IS metro, so a flag-off key and a
  // flag-on metro key are deliberately the same entry.
  const geo = `geo:${tax.geoTax ?? 1}`;
  // The CAPABILITY, not just the taxonomy. `activitiesEnabled` decides whether
  // the backend runs activity extraction at all, so the same prompt under the
  // same taxonomies resolves to DIFFERENT filters on an activity-capable page
  // than on one without the filter. Keyed separately from `activitiesKey`,
  // which only identifies which activity tree would be used if it ran.
  const act = `act:${activitiesEnabled ? 'on' : 'off'}`;
  if (filterKind === 'screener') {
    return `${prompt}\u0001screener\u0001${geo}\u0001${act}`;
  }
  return `${prompt}\u0001${tax.rolesKey}\u0001${tax.skillsKey}\u0001${tax.activitiesKey}\u0001tdV2\u0001${geo}\u0001${act}`;
}

const inFlight = new Map<string, FilterPromise>();

function readStore(): StoredEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is StoredEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as StoredEntry).key === 'string' &&
        typeof (e as StoredEntry).cacheVersion === 'string' &&
        (e as StoredEntry).cacheVersion === CACHE_VERSION &&
        (e as StoredEntry).resolved !== undefined
    );
  } catch {
    return [];
  }
}

function writeStore(entries: StoredEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota errors / private browsing: non-fatal.
  }
}

function readStoredFresh(
  key: string
): GlobalAssistantResolvedRouteFilters | null {
  const entry = readStore().find((e) => e.key === key);
  return entry?.resolved ?? null;
}

function writeStored(
  key: string,
  resolved: GlobalAssistantResolvedRouteFilters
): void {
  const entries = readStore().filter((e) => e.key !== key);
  entries.push({ key, resolved, cacheVersion: CACHE_VERSION });
  const overflow = entries.length - MAX_ENTRIES;
  writeStore(overflow > 0 ? entries.slice(overflow) : entries);
}

type FetchOptions = {
  bypassCache?: boolean;
  filterKind?: GlobalAssistantRouteFilterKind;
  /**
   * Screener cache partition. Company/geography/role Discovery currently share
   * one endpoint contract, but the cache key keeps entity scope explicit so a
   * future endpoint split cannot silently serve cross-entity stale responses.
   */
  screenerEntityType?: 'company' | 'geography' | 'role';
  /**
   * The target's pages have the activity filter (always, except on Company
   * pages, which need `activity-taxonomy-company`). Threaded in
   * from the engine's single flag read (`activityFilterFlags`) rather than
   * read from Unleash again here — this module is not a component and a
   * second read would be a second source of truth for one capability. Sent
   * only when definitively true; absent means no.
   */
  activitiesEnabled?: boolean;
};

function resolveFilterKind(
  options: FetchOptions | undefined
): GlobalAssistantRouteFilterKind {
  return options?.filterKind ?? 'tdV2';
}

function resolveScreenerEntityType(
  options: FetchOptions | undefined
): 'company' | 'geography' | 'role' | 'unknown' {
  return options?.screenerEntityType ?? 'unknown';
}

/**
 * Start resolving filters for `prompt` in the background.
 * Returns a (possibly already-running or already-resolved) promise.
 */
function prefetchRouteFilters(
  prompt: string,
  taxonomy: AssistantTaxonomy,
  options?: FetchOptions
): FilterPromise {
  const normalized = prompt.trim();
  if (normalized.length === 0) {
    return Promise.reject(new Error('Empty prompt'));
  }
  const filterKind = resolveFilterKind(options);
  // See `buildKey`: the capability partitions the cache because it decides
  // whether activity extraction runs at all, so it belongs on BOTH branches.
  const activitiesEnabled = options?.activitiesEnabled === true;
  const key =
    filterKind === 'screener'
      ? `${normalized}\u0001screener\u0001${resolveScreenerEntityType(options)}\u0001geo:${taxonomy.geoTax ?? 1}\u0001act:${activitiesEnabled ? 'on' : 'off'}`
      : buildKey(normalized, taxonomy, filterKind, activitiesEnabled);

  if (!options?.bypassCache) {
    const existing = inFlight.get(key);
    if (existing) return existing;

    const stored = readStoredFresh(key);
    if (stored !== null) {
      const resolved = Promise.resolve(stored);
      inFlight.set(key, resolved);
      return resolved;
    }
  }

  const promise = (() => {
    if (filterKind === 'screener') {
      return screenerAiSearchRequest({
        prompt: normalized,
        ...(taxonomy.geoTax !== undefined ? { geoTax: taxonomy.geoTax } : {}),
      }).then(({ response }) => {
        const payload: GlobalAssistantResolvedRouteFilters = {
          filterKind: 'screener',
          response,
        };
        writeStored(key, payload);
        return payload;
      });
    }
    return aiFilterSearchRequest({
      prompt: normalized,
      rolesTax: taxonomy.rolesTax,
      skillsTax: taxonomy.skillsTax,
      rolesTaxAdaptive: taxonomy.rolesKey === 'adaptive',
      skillsTaxAdaptive: taxonomy.skillsKey === 'adaptive',
      activitiesTax: taxonomy.activitiesTax,
      activitiesTaxAdaptive: taxonomy.activitiesKey === 'adaptive',
      ...(options?.activitiesEnabled === true
        ? { activitiesEnabled: true }
        : {}),
      ...(taxonomy.geoTax !== undefined ? { geoTax: taxonomy.geoTax } : {}),
      // Talent Discovery (the tdV2 target) uses a monthly axis; anchor relative
      // date windows to the latest complete month, matching the in-page form.
      // This prefetch runs before navigation so it can't read the page's
      // granularity from context, but TD is always monthly (td_users metric).
      dateGranularity: 'month',
    }).then(({ response }) => {
      const payload: GlobalAssistantResolvedRouteFilters = {
        filterKind: 'tdV2',
        response,
      };
      writeStored(key, payload);
      return payload;
    });
  })().finally(() => {
    if (inFlight.get(key) === promise) {
      inFlight.delete(key);
    }
  });

  inFlight.set(key, promise);
  return promise;
}

/** Alias — semantically "get from cache, fallback to fetch". */
export function getRouteFilterResponse(
  prompt: string,
  taxonomy: AssistantTaxonomy,
  options?: FetchOptions
): FilterPromise {
  return prefetchRouteFilters(prompt, taxonomy, options);
}

/** Test hook — reset both tiers. */
export function __resetRouteFilterPrefetchCacheForTests(): void {
  inFlight.clear();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}
